import SalviaLayout from "@/components/SalviaLayout";
import TrainingVideoPlayer from "@/components/TrainingVideoPlayer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/hooks/usePageTitle";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { BookOpenText, Layers3 } from "lucide-react";

export default function PortalTrainingCenter() {
  usePageTitle("Central de Treinamentos");
  const { user } = useAuth();
  const workspaceQuery = trpc.trainingVideos.getWorkspace.useQuery(undefined, {
    enabled: !!user,
    staleTime: 30_000,
  });

  const videos = workspaceQuery.data?.libraryVideos ?? [];
  const roleLabel = workspaceQuery.data?.hasPeopleBaseAccess
    ? "Conteúdo liberado conforme o seu acesso operacional"
    : "Conteúdo geral do portal";

  return (
    <SalviaLayout title="Central de Treinamentos">
      <div className="space-y-6">
        <section className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-sm lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <Layers3 className="h-3.5 w-3.5 text-[#C0FF00]" />
              Biblioteca de apoio
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-white lg:text-4xl">
                Vídeos de treinamento do Portal da Jornada do Cliente
              </h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-300 lg:text-base">
                Assista aos treinamentos diretamente no portal. Os vídeos exibidos aqui respeitam o seu perfil e os módulos liberados para o seu contexto.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Badge variant="secondary" className="rounded-full bg-white/10 px-3 py-1 text-white">
                {videos.length} vídeo(s) disponível(is)
              </Badge>
              <Badge variant="secondary" className="rounded-full bg-white/10 px-3 py-1 text-white">
                {roleLabel}
              </Badge>
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Treinamentos publicados</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-950">Escolha um vídeo para assistir</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                A reprodução acontece dentro do próprio PJC, sem depender de download como ação principal.
              </p>
            </div>
          </div>

          {workspaceQuery.isLoading ? (
            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <Skeleton className="h-6 w-40 rounded-xl" />
                  <Skeleton className="mt-4 h-6 w-72 rounded-xl" />
                  <Skeleton className="mt-3 h-16 w-full rounded-2xl" />
                  <Skeleton className="mt-4 h-10 w-36 rounded-xl" />
                </div>
              ))}
            </div>
          ) : videos.length > 0 ? (
            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              {videos.map((video: any) => (
                <TrainingVideoPlayer
                  key={video.id ?? video.slug}
                  video={video}
                  eyebrow={video.placement === "login" ? "Boas-vindas" : "Treinamento operacional"}
                  ctaLabel="Assistir"
                  compact
                />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-7 text-slate-600">
              <div className="flex items-start gap-3">
                <BookOpenText className="mt-1 h-5 w-5 text-slate-400" />
                <div>
                  <p className="font-semibold text-slate-900">Ainda não há vídeos liberados para este perfil.</p>
                  <p className="mt-2">
                    Assim que novos treinamentos forem publicados ou liberados para o seu contexto, eles passarão a aparecer nesta biblioteca.
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>

      </div>
    </SalviaLayout>
  );
}
